import { Link } from 'react-router-dom';
import { Tag, Image } from 'antd';
import { ResourceList } from '@/components/ResourceList';
import { listTasks, toOutputImages, type Task } from '@/api/tasks';
import { formatDateTime, truncate } from '@/utils/format';

const statusColor: Record<string, string> = {
  done: 'green',
  running: 'blue',
  queued: 'gold',
  failed: 'red',
  cancelled: 'default',
};

// firstThumb: 取 outputs 第一张图的缩略图 url (无则原图 url)。
// outputs 可能是对象 {images/image_urls} 或数组 → 经 toOutputImages 归一。
function firstThumb(outputs?: Task['outputs']): string | undefined {
  for (const o of toOutputImages(outputs)) {
    const u = o.thumb_url || o.url;
    if (u) return u;
  }
  return undefined;
}

export function TasksListPage() {
  return (
    <ResourceList<Task>
      title="任务"
      description="跨用户生成任务 (只读)；写真套系 + 创作工具 (image_edit/beauty) 同表。cursor pagination。"
      rowKey="id"
      pagination="cursor"
      filters={[
        { key: 'user_id', label: '用户 ID', type: 'text', placeholder: 'usr_...' },
        { key: 'app_id', label: 'App ID', type: 'text', placeholder: '数字' },
        {
          key: 'workflow_code',
          label: '工作流',
          type: 'text',
          placeholder: 'soul_animal / image_edit / beauty ...',
        },
        {
          key: 'status',
          label: '状态',
          type: 'select',
          options: [
            { value: 'queued', label: '排队中' },
            { value: 'running', label: '生成中' },
            { value: 'done', label: '完成' },
            { value: 'failed', label: '失败' },
            { value: 'cancelled', label: '已取消' },
          ],
        },
      ]}
      fetcher={({ filters, pageSize, cursor }) => {
        const f = { ...filters } as Record<string, unknown>;
        if (typeof f.app_id === 'string') f.app_id = Number(f.app_id);
        return listTasks({ ...f, cursor, limit: pageSize } as never);
      }}
      columns={[
        {
          title: '任务 ID',
          dataIndex: 'id',
          render: (v) => <Link to={`/tasks/${v}`}>{truncate(v, 16)}</Link>,
        },
        { title: '用户', dataIndex: 'user_id', render: (v) => truncate(v, 12) },
        { title: 'App', dataIndex: 'app_id', render: (v) => <Tag color="blue">app#{v}</Tag> },
        { title: '工作流', dataIndex: 'workflow_code', render: (v) => <Tag>{v}</Tag> },
        {
          title: '套系',
          dataIndex: 'package_id',
          // 显示套系名 (package_name); id 作 title/tooltip。无套系则 '-'。
          render: (v, row) =>
            v ? <span title={`#${v}`}>{row.package_name || `#${v}`}</span> : '-',
        },
        {
          title: '状态',
          dataIndex: 'status',
          render: (v) => <Tag color={statusColor[v] || 'default'}>{v}</Tag>,
        },
        {
          title: '结果图',
          dataIndex: 'outputs',
          render: (_v, row) => {
            const thumb = firstThumb(row.outputs);
            return thumb ? (
              <Image
                src={thumb}
                width={40}
                height={40}
                style={{ objectFit: 'cover', borderRadius: 4 }}
                preview={{ src: toOutputImages(row.outputs)[0]?.url || thumb }}
              />
            ) : (
              '-'
            );
          },
        },
        {
          title: '创建时间',
          dataIndex: 'created_at',
          render: (v) => formatDateTime(v),
        },
      ]}
    />
  );
}
